import { useEffect, useRef } from 'react'
import useSWR from 'swr'
import { apiGetPreferences, apiUpdatePreferences } from '@/services/AuthService'
import type { UserPreferences } from '@/@types/trackheros'

/**
 * Records the browser's time zone on the account the first time, the way
 * `LocaleSync` records the language.
 *
 * One direction only: the account's value, once set, is the person's choice
 * (Settings) and nothing here overrides it. A blank means nobody has ever
 * said, so the browser's zone is the best information anyone has — it seeds
 * a host's first availability schedule for booking links, and dates the
 * documents the exporters write.
 *
 * Shares the `preferences` SWR key with `LocaleSync` and Settings, so the
 * request is made once.
 */
const TimezoneSync = () => {
    const { data } = useSWR<UserPreferences>(
        ['preferences'],
        () => apiGetPreferences(),
        { revalidateOnFocus: false },
    )
    const done = useRef(false)

    useEffect(() => {
        if (!data || done.current) {
            return
        }
        done.current = true
        if (data.timezone) {
            return
        }
        let guess = ''
        try {
            guess = Intl.DateTimeFormat().resolvedOptions().timeZone ?? ''
        } catch {
            return
        }
        if (!guess) {
            return
        }
        apiUpdatePreferences({ timezone: guess }).catch(() => {
            // Best effort: the next sign-in records it instead.
        })
    }, [data])

    return null
}

export default TimezoneSync
